import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";
import { controlLoading, controlTabBar } from "@/services/slices/app.slice";
import { authenticate, autoLogin } from "@/services/slices/auth.slice";
import { loginAPI } from "@/services/api/user/login";
import { GetAccessTokenAPI } from "@/services/api/user/accessToken";
import { config } from "@/config";

export type LoginType = "student" | "business" | "admin";

/**
 * Web port of the RN `useLoginScreenHooks`. Socket.IO / analytics / chat
 * registration are intentionally omitted (out of scope for the web app).
 */
export function useLoginHooks(initialLoginType?: LoginType) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [modalAlertVisible, setModalAlertVisible] = useState<boolean>(false);
  const [modalAlertText, setModalAlertText] = useState<string>("");
  const [loginType, setLoginType] = useState<LoginType>(initialLoginType || "student");

  const showModalAlert = (text: string) => {
    setModalAlertText(text);
    setModalAlertVisible(true);
  };

  const hideModalAlert = () => setModalAlertVisible(false);

  // Student login handler
  const handleStudentLogin = async () => {
    setShowValidation(true);
    if (!email || !password) {
      showModalAlert(i18n.t("Please fill in all the blank"));
      return;
    }
    dispatch(controlLoading({ showLoading: true }));
    const resp = await loginAPI(email, password, "student");
    dispatch(controlLoading({ showLoading: false }));
    if (resp?.status === undefined || resp.code === "0")
      return showModalAlert(i18n.t("Please Try Again"));
    if (resp.status === 2) return showModalAlert(i18n.t("Please Try Again"));
    if (resp.status === -20003)
      return showModalAlert(i18n.t("Email Not Registered"));
    if (resp.status === -20004)
      return showModalAlert(i18n.t("Invalid Password"));
    if (resp.status === -20005)
      return showModalAlert(i18n.t("Account Is Not Active"));
    if (resp.status === 0) {
      const userAuth = resp.result.user?.role ?? resp.result.role;
      const normalizedUser = {
        id: resp.result.user_id ?? "",
        profile_icon: resp.result.profile_icon ?? "",
        student_card: resp.result.student_card ?? "",
        username: resp.result.user?.username ?? resp.result.username ?? "",
        university_email: resp.result.university_email ?? "",
        gender: resp.result.gender ?? "",
        university: resp.result.university ?? "",
        university_number: resp.result.university_number ?? "",
        year_of_study: resp.result.year_of_study ?? "",
        identity: resp.result.identity ?? "",
        phone_num: resp.result.phone_num ?? "",
        bachelor_degree_id: resp.result.bachelor_degree_id ?? "",
        bachelor_degree: resp.result.bachelor_degree ?? "",
        expiry_date: resp.result.expiry_date ?? "",
        society_id: resp.result.society_id ?? [],
        user_auth: userAuth,
        active: resp.result.user?.active ?? resp.result.active ?? "",
        deleted: resp.result.user?.deleted ?? resp.result.deleted ?? "",
        access_token: resp.result.user?.authentication?.access_token ?? "",
        utime: resp.result.utime ?? "",
        ctime: resp.result.ctime ?? "",
      };
      const accessToken = await GetAccessTokenAPI(
        resp.result.user_id,
        resp.result.university_email,
      );
      dispatch(
        autoLogin({
          userType: "student",
          user: normalizedUser,
          access_token: accessToken,
        }),
      );
      config.ACCESS_TOKEN = accessToken;
      dispatch(authenticate({ loggedIn: true, user_auth: userAuth }));
      dispatch(
        controlTabBar({
          home: true,
          calendar: false,
          survey: false,
          activtiy: false,
          redeem: false,
          qrcodeScan: false,
          chat: false,
          profile: false,
        }),
      );
      navigate("/student/home");
    }
  };

  // Business login handler
  const handleBusinessLogin = async () => {
    setShowValidation(true);
    if (!email || !password) {
      showModalAlert(i18n.t("Please fill in all the blank"));
      return;
    }
    dispatch(controlLoading({ showLoading: true }));
    const resp = await loginAPI(email, password, "business");
    dispatch(controlLoading({ showLoading: false }));
    if (resp?.status === undefined || resp.code === "0")
      return showModalAlert(i18n.t("Please Try Again"));
    if (resp.status === 2) return showModalAlert(i18n.t("Please Try Again"));
    if (resp.status === -21003)
      return showModalAlert(i18n.t("Business Not Found or Inactive"));
    if (resp.status === -21004)
      return showModalAlert(i18n.t("Business Invalid Password"));
    if (resp.status === -21005)
      return showModalAlert(i18n.t("Business Account Is Not Active"));
    if (resp.status === -21006)
      return showModalAlert(i18n.t("Your business is awaiting admin approval"));
    if (resp.status === -21007)
      return showModalAlert(i18n.t("Your business registration was rejected"));
    if (resp.status === 0) {
      const userAuth = resp.result.user?.role ?? resp.result?.role ?? "";
      const normalizedBusiness = {
        id: resp.result.id,
        user_id: resp.result.user_id,
        br_image: resp.result.br_image,
        br_number: resp.result.br_number,
        company_address: resp.result.company_address,
        company_email: resp.result.company_email,
        company_name: resp.result.company_name,
        contact_number: resp.result.contact_number,
        user_auth: userAuth,
        utime: resp.result.utime,
        ctime: resp.result.ctime,
      };
      const accessToken = await GetAccessTokenAPI(
        resp.result.user_id,
        resp.result.company_email,
      );
      dispatch(
        autoLogin({
          userType: "business",
          user: normalizedBusiness,
          access_token: accessToken,
        }),
      );
      config.ACCESS_TOKEN = accessToken;
      dispatch(authenticate({ loggedIn: true, user_auth: userAuth }));
      dispatch(
        controlTabBar({
          home: false,
          calendar: false,
          survey: false,
          activtiy: false,
          redeem: false,
          qrcodeScan: false,
          chat: false,
          profile: false,
        }),
      );
      navigate("/portal");
    }
  };

  // Admin login handler
  const handleAdminLogin = async () => {
    setShowValidation(true);
    if (!email || !password) {
      showModalAlert(i18n.t("Please fill in all the blank"));
      return;
    }
    dispatch(controlLoading({ showLoading: true }));
    const resp = await loginAPI(email, password, "admin");
    dispatch(controlLoading({ showLoading: false }));
    if (resp?.status === undefined || resp.code === "0")
      return showModalAlert(i18n.t("Please Try Again"));
    if (resp.status === 2) return showModalAlert(i18n.t("Please Try Again"));
    if (resp.status === -22003)
      return showModalAlert(i18n.t("Admin Not Found or Inactive"));
    if (resp.status === -22004)
      return showModalAlert(i18n.t("Admin Invalid Password"));
    if (resp.status === -22005)
      return showModalAlert(i18n.t("Admin Account Is Not Active"));
    if (resp.status === 0) {
      const userAuth = resp.result?.role ?? resp.result?.role;
      const authData = resp.result.authentication || {};
      const { password: _pw, ...safeAuth } = authData;
      const normalizedAdmin = {
        id: resp.result.id,
        username: resp.result.username,
        role: resp.result.role,
        active: resp.result.active,
        deleted: resp.result.deleted,
        authentication: safeAuth,
      };
      const accessToken = await GetAccessTokenAPI(
        resp.result.id,
        resp.result.university_email,
      );
      dispatch(
        autoLogin({
          userType: "admin",
          user: normalizedAdmin,
          access_token: accessToken,
        }),
      );
      config.ACCESS_TOKEN = accessToken;
      dispatch(authenticate({ loggedIn: true, user_auth: userAuth }));
      dispatch(
        controlTabBar({
          home: false,
          calendar: false,
          survey: false,
          activtiy: false,
          redeem: false,
          qrcodeScan: false,
          chat: false,
          profile: false,
          userManagement: true,
          adminSocieties: false,
          adminSponsors: false,
        }),
      );
      navigate("/console/dashboard");
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    showValidation,
    setShowValidation,
    modalAlertVisible,
    setModalAlertVisible,
    modalAlertText,
    setModalAlertText,
    showModalAlert,
    hideModalAlert,
    handleStudentLogin,
    handleBusinessLogin,
    handleAdminLogin,
    loginType,
    setLoginType,
  };
}